import {
  type Project,
  type Field,
  type Dataset,
  type Choice,
  type Value,
  type Values,
  datasetSchema,
  orderedDataset,
  assertDataset,
  parseProject,
  safePattern,
} from "./schema";
export function datasetVersion(
  project: Project,
  source: Field["source"],
): Dataset {
  const versions = project.datasets.filter(
    (d) =>
      d.id === source.datasetId &&
      (!source.datasetVersion || d.version === source.datasetVersion),
  );
  if (versions.length !== 1)
    throw new Error("Dataset version is missing or ambiguous");
  return versions[0];
}
export function datasetChoices(
  dataset: Dataset,
  source: Field["source"],
): Choice[] {
  dataset = orderedDataset(dataset);
  if (dataset.kind === "choices") return structuredClone(dataset.choices);
  const valueColumn =
      source.valueColumn || dataset.valueColumn || dataset.primaryKey!,
    labelColumn = source.labelColumn || dataset.labelColumn || valueColumn;
  return dataset.rows.map((row) => ({
    value: String(row[valueColumn] ?? ""),
    label: String(row[labelColumn] ?? ""),
    ...(dataset.imageColumn && row[dataset.imageColumn]
      ? { image: String(row[dataset.imageColumn]) }
      : {}),
    ...(dataset.assetColumn && row[dataset.assetColumn]
      ? { assetId: String(row[dataset.assetColumn]) }
      : {}),
  }));
}
export function lookupDataset(
  project: Project,
  field: Field,
  values: Values,
  visible: Set<string>,
): Value {
  const source = field.source,
    selector = source.lookupFieldId!;
  if (
    !visible.has(selector) ||
    values[selector] === null ||
    values[selector] === undefined ||
    values[selector] === ""
  )
    return field.default;
  const dataset = datasetVersion(project, source),
    match = source.matchColumn || dataset.primaryKey!,
    key = String(values[selector]);
  const row = dataset.rows.find(
    (row) =>
      row[match] !== null &&
      row[match] !== undefined &&
      (source.matchOperator === "startsWith"
        ? key.startsWith(String(row[match]))
        : source.matchOperator === "endsWith"
          ? key.endsWith(String(row[match]))
          : source.matchOperator === "contains"
            ? key.includes(String(row[match]))
            : source.matchOperator === "matches"
              ? safePattern(String(row[match])).test(key.slice(0, 32768))
              : String(row[match]) === key),
  );
  return row?.[source.resultColumn!] ?? field.default;
}
export function addDatasetVersion(
  project: Project,
  definition: unknown,
  expectedVersion: number,
): Project {
  project = parseProject(project);
  const incoming = datasetSchema.parse(definition);
  assertDataset(incoming);
  const existing = project.datasets.filter((d) => d.id === incoming.id),
    latest = Math.max(0, ...existing.map((d) => d.version));
  if (latest !== expectedVersion)
    throw new Error("Dataset changed since it was loaded");
  return parseProject({
    ...project,
    datasets: [...project.datasets, { ...incoming, version: latest + 1 }],
  });
}
